import { paymentStatementApi } from './base';

export interface TeamMember {
	id: string;
	email: string;
	name: string;
	role: string;
	status: string;
	created_at: string;
	invited_by?: string;
	expires_at?: string;
}

export interface PaginationInfo {
	page: number;
	per_page: number;
	total: number;
	pages: number;
}

export interface StatusCounts {
	all: number;
	active: number;
	inactive: number;
	invited: number;
	pending_verification: number;
	suspended: number;
	expired: number;
	cancelled: number;
}

export interface TeamResponse {
	members: TeamMember[];
	isTeamAdmin: boolean;
	pagination: PaginationInfo;
	statusCounts: StatusCounts;
}

export interface InviteRequest {
	email: string;
	name?: string;
	role?: string;
}

export interface InviteResponse {
	success: boolean;
	message: string;
	invite?: any;
}

export interface UpdateUserRequest {
	name?: string;
	role?: string;
}

export interface UpdateUserResponse {
	success: boolean;
	message: string;
	user?: any;
}

export interface DeactivateUserResponse {
	success: boolean;
	message: string;
}

export interface UserSearchParams {
	status?: string;
	page?: number;
	per_page?: number;
	search?: string;
}

export const teamsApi = paymentStatementApi.injectEndpoints({
	endpoints: (builder) => ({
		getTeamMembers: builder.query<TeamResponse, UserSearchParams>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				if (params.status && params.status !== 'all') {
					queryParams.append('status', params.status);
				}
				if (params.page && params.page > 1) {
					queryParams.append('page', params.page.toString());
				}
				if (params.per_page && params.per_page !== 20) {
					queryParams.append('per_page', params.per_page.toString());
				}
				if (params.search && params.search.trim()) {
					queryParams.append('search', params.search.trim());
				}
				const queryString = queryParams.toString();
				return {
					url: `/auth/users${queryString ? `?${queryString}` : ''}`,
					method: 'GET',
				};
			},
			providesTags: ['TeamMembers'],
		}),

		inviteTeamMember: builder.mutation<InviteResponse, InviteRequest>({
			query: (body) => ({
				url: '/auth/invite',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['TeamMembers'],
		}),

		cancelInvite: builder.mutation<
			{ success: boolean; message: string },
			string
		>({
			query: (inviteId) => ({
				url: `/auth/invites/${inviteId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['TeamMembers'],
		}),

		updateUser: builder.mutation<
			UpdateUserResponse,
			{ userId: string; data: UpdateUserRequest }
		>({
			query: ({ userId, data }) => ({
				url: `/auth/users/${userId}`,
				method: 'PUT',
				body: data,
			}),
			invalidatesTags: ['TeamMembers'],
		}),

		deactivateUser: builder.mutation<DeactivateUserResponse, string>({
			query: (userId) => ({
				url: `/auth/users/${userId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['TeamMembers'],
		}),

		reactivateUser: builder.mutation<DeactivateUserResponse, string>({
			query: (userId) => ({
				url: `/auth/users/${userId}/reactivate`,
				method: 'POST',
			}),
			invalidatesTags: ['TeamMembers'],
		}),
	}),
});

export const {
	useGetTeamMembersQuery,
	useInviteTeamMemberMutation,
	useCancelInviteMutation,
	useUpdateUserMutation,
	useDeactivateUserMutation,
	useReactivateUserMutation,
} = teamsApi;
